import type { Locale } from "@/i18n/locales";
import {
  MP_BUYER_STEPS,
  MP_FEATURING,
  MP_HIW_FAQ,
  MP_HIW_GLOSSARY,
  MP_HIW_INTRO,
  MP_HIW_RULES,
  MP_SELLER_STEPS,
} from "./how-it-works-content";
import { esMpHowItWorks } from "./how-it-works/es";
import { deMpHowItWorks } from "./how-it-works/de";
import { frMpHowItWorks } from "./how-it-works/fr";
import { itMpHowItWorks } from "./how-it-works/it";
import { ruMpHowItWorks } from "./how-it-works/ru";
import { ptBrMpHowItWorks } from "./how-it-works/pt-br";
import { arMpHowItWorks } from "./how-it-works/ar";

export type MpHowItWorksContent = {
  intro: string;
  buyerSteps: { title: string; body: string }[];
  sellerSteps: { title: string; body: string }[];
  featuring: {
    intro: string;
    plans: { label: string; heat: number; days: number; blurb: string }[];
    points: string[];
  };
  rules: string[];
  glossary: [string, string][];
  faq: [string, string][];
};

/** Localised text only: keys, links, plan numbers and ordering always come from the English source. */
export type MpHowItWorksTranslation = {
  intro?: string;
  buyerSteps?: { title: string; body: string }[];
  sellerSteps?: { title: string; body: string }[];
  featuring?: {
    intro?: string;
    plans?: { blurb: string }[];
    points?: string[];
  };
  rules?: string[];
  glossary?: [string, string][];
  faq?: [string, string][];
};

const EN: MpHowItWorksContent = {
  intro: MP_HIW_INTRO,
  buyerSteps: MP_BUYER_STEPS,
  sellerSteps: MP_SELLER_STEPS,
  featuring: MP_FEATURING,
  rules: MP_HIW_RULES,
  glossary: MP_HIW_GLOSSARY,
  faq: MP_HIW_FAQ,
};

const TRANSLATIONS: Partial<Record<Locale, MpHowItWorksTranslation>> = {
  es: esMpHowItWorks,
  de: deMpHowItWorks,
  fr: frMpHowItWorks,
  it: itMpHowItWorks,
  ru: ruMpHowItWorks,
  "pt-br": ptBrMpHowItWorks,
  ar: arMpHowItWorks,
};

export function getMpHowItWorksContent(locale: Locale): MpHowItWorksContent {
  if (locale === "en") return EN;
  const t = TRANSLATIONS[locale];
  if (!t) return EN;
  return {
    intro: t.intro ?? EN.intro,
    buyerSteps: t.buyerSteps?.length === EN.buyerSteps.length ? t.buyerSteps : EN.buyerSteps,
    sellerSteps: t.sellerSteps?.length === EN.sellerSteps.length ? t.sellerSteps : EN.sellerSteps,
    featuring: {
      intro: t.featuring?.intro ?? EN.featuring.intro,
      plans:
        t.featuring?.plans?.length === EN.featuring.plans.length
          ? EN.featuring.plans.map((p, i) => ({
              ...p,
              blurb: t.featuring?.plans?.[i]?.blurb ?? p.blurb,
            }))
          : EN.featuring.plans,
      points: t.featuring?.points?.length === EN.featuring.points.length ? t.featuring!.points : EN.featuring.points,
    },
    rules: t.rules?.length === EN.rules.length ? t.rules : EN.rules,
    glossary: t.glossary?.length === EN.glossary.length ? t.glossary : EN.glossary,
    faq: t.faq?.length === EN.faq.length ? t.faq : EN.faq,
  };
}
